<!-- DropDownButtonItemsWithFilter.vue -->
<!-- Use to generate list of drop-down items with a filter input when we hit a specified number of items -->
<script setup lang="ts" generic="TItem">
    import TextInput from '../TextInput.vue';
    import Icon from '../Icon.vue';
    import { DisplayStyle, IconTypes } from '../../Enums/UiEnums';
    import { computed, reactive } from 'vue';
    import DropDownButtonItem from '../DropDownButtonItem.vue';

    const defaultFilterSplitCharacters = " ,.;-!?";
    const defaultFilterLengthThreshold = 2;

    const props = withDefaults(defineProps<{
        items: TItem[] | undefined,
        showFilterItemCount?: number,
        filterSplitCharacters?: string,
        filterLengthThreshold?: number,
        toFilterText?: (item: TItem) => string  // Used when TItem is an object to get searchable text
    }>(), {
        showFilterItemCount: 10,
        filterSplitCharacters: defaultFilterSplitCharacters,
        filterLengthThreshold: defaultFilterLengthThreshold
    });

    const emits = defineEmits<
    {
        itemClicked: [item: TItem]
    }>();

    const compData = reactive<{
        searchText?: string
    }>({});

    const lowerCaseFilterParts = computed(() => compData.searchText?.toLowerCase().split(props.filterSplitCharacters ?? defaultFilterSplitCharacters));

    const visibleItems = computed(() =>{
        // Min number of characters to match
        if ((compData.searchText?.length ?? 0) < (props.filterLengthThreshold ?? defaultFilterLengthThreshold))
            return props.items;
        
        const visibleItems = props.items?.filter(item => showItem(item));
        return visibleItems;
    });

    function showItem(item: TItem): boolean
    {
        if ("string" === typeof(item))
        {
            return isPartsMatch(item);
        }

        if ("number" === typeof(item))
        {
            const lowerCaseItem = item.toString().toLowerCase();
            if (compData.searchText && -1 === lowerCaseItem.indexOf(compData.searchText))
                return false;
            return true;
        }

        if ("object" === typeof(item) && undefined !== props.toFilterText)
        {
            return isPartsMatch(props.toFilterText(item));
        }

        if (undefined === item)
        {
            return false;
        }

        // Don't know how to match so don't bother with filter
        return true;
    }

    function isPartsMatch(itemText: string): boolean
    {
        if (!lowerCaseFilterParts.value)
            return false;

        const lowerCaseItem = itemText.toLowerCase();
        for(const filterPart of lowerCaseFilterParts.value)
        {
            if (-1 === lowerCaseItem.indexOf(filterPart))
                return false;
        }
        return true;
    }
</script>

<template>

    <!-- Add a filter to the top of the list of items -->
    <template v-if="(props.items?.length ?? 0) > props.showFilterItemCount">
        <DropDownButtonItem>
            <TextInput @update:textValue="txt => compData.searchText = txt?.toString()">
                <template #LeftAddon>
                    <Icon :icon="IconTypes.Search" :displayStyle="DisplayStyle.Dark" />
                </template>
            </TextInput>
        </DropDownButtonItem>    
    </template>

    <!-- List of visible items -->
    <DropDownButtonItem v-for="item in visibleItems" @click="emits('itemClicked', item)">
        <slot name="item" :item="item">
            {{ item }}
        </slot>
    </DropDownButtonItem>

</template>